class TagsService {
  constructor(
    Restangular,
    Authentication,
    SlingApi,
    PremiumDiscoveryService,
    $injector,
    $rootScope,
    sdk
  ) {
    'ngInject';

    Object.assign(this, {
      Restangular,
      Authentication,
      SlingApi,
      PremiumDiscoveryService,
      $injector,
      $rootScope,
      sdk,
    });

    this.SHIFT_BLOCK_SOURCES = sdk('constants').SHIFT_BLOCK_SOURCES;
    this.getShiftAppearanceToDisplay = sdk('helpers/getShiftAppearanceToDisplay');
    this.extendTag = sdk('model-extensions/extendTagModel');

    this._promise = null;
    this.tags = [];
    this.onChangeCallback = null;

    this.initSockets();
  }

  get isTagFeatureHidden() {
    return (
      this.Authentication.isOrgFeatureEnabled('tags') &&
      !this.Authentication.user.settings.clientFlags.tagsFeatureEnabled
    );
  }

  registerOnChangeCallback(callback) {
    this.onChangeCallback = callback;
  }

  async loadData(callback) {
    if (!this.Authentication.isOrgFeatureEnabled('tags')) {
      this._toggleTags();
      return false;
    }

    if (!this._promise) {
      this._promise = this._loadFromServer(callback);
    }

    try {
      return await this._promise;
    } catch (error) {
      this._promise = null;

      throw error;
    }
  }

  async _loadFromServer() {
    const data = await this.SlingApi.getTags();

    if (data && data.length) {
      this._fillList(data);
    }
    this._toggleTags();

    return this.tags;
  }

  async saveTag(tag) {
    let request;

    if (tag.id) {
      request = this.SlingApi.updateTag(tag);
    } else {
      request = this.SlingApi.createTag(tag);
    }

    const savedTag = await request;
    this._updateList(savedTag);

    this.PremiumDiscoveryService.featureDiscovered(this.PremiumDiscoveryService.FEATURE_NAMES.TAGS);

    return savedTag;
  }

  async deleteTag(tag) {
    if (tag.id) {
      await this.SlingApi.deleteTag(tag.id);
      this._deleteFromList(tag);
      this._syncEvents();
    }
  }

  async deleteTagsBulk(deleteIds) {
    const idsArray = Object.keys(deleteIds)
      .filter((k) => deleteIds[k])
      .map(Number);

    if (!idsArray.length) {
      return false;
    }

    const tagsToDelete = idsArray.map((id) => ({ id, deleted: true }));
    await this.SlingApi.deleteTags(tagsToDelete);

    tagsToDelete.forEach((tag) => this._deleteFromList(tag));
    this._syncEvents();
  }

  _fillList(tags) {
    this.tags.length = 0;
    tags.forEach((tag) => this.tags.push(this.extendTag(tag)));

    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

  _updateList(tag) {
    const index = this.tags.findIndex((t) => t.id === tag.id);
    const extendedTag = this.extendTag(tag);

    if (index > -1) {
      this.tags[index] = extendedTag;
      this._syncEvents();
    } else {
      this.tags.push(extendedTag);
    }

    this._toggleTags();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

  _deleteFromList(tag) {
    const index = this.tags.findIndex((t) => t.id === tag.id);

    if (index < 0) {
      return;
    }

    this.tags.splice(index, 1);

    this._toggleTags();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

  _toggleTags() {
    if (__TEST__) {
      return;
    }

    const shouldBeEnabled = !!this.tags.length;
    const { tagsFeatureEnabled } = this.Authentication.user.settings.clientFlags;

    if (shouldBeEnabled === tagsFeatureEnabled) {
      return;
    }

    this._adjustShiftAppearanceSettings(shouldBeEnabled);
    this.Authentication.user.settings.clientFlags.tagsFeatureEnabled = shouldBeEnabled;
    this.Authentication.syncSettings();
  }

  _adjustShiftAppearanceSettings(enabled = true) {
    const updatedShiftAppearance = this.getShiftAppearanceToDisplay(true);

    if (enabled) {
      this._addDefaultTagAppearance(updatedShiftAppearance, 'day');
      this._addDefaultTagAppearance(updatedShiftAppearance, 'week');
      this._addDefaultTagAppearance(updatedShiftAppearance, 'month', 'fourthLine');
    }

    this.Authentication.user.settings.shiftAppearance = updatedShiftAppearance;
  }

  _addDefaultTagAppearance(shiftAppearance, mode, line = 'thirdLine') {
    shiftAppearance[mode][line] = [this.SHIFT_BLOCK_SOURCES.TAG];
  }

  _syncEvents() {
    this.$injector.get('EventCollection').syncEvents();
  }

  refresh() {
    this._promise = null;
    return this.loadData();
  }

  initSockets() {
    this.$rootScope.$on('socket:tag:new', (event, data) => {
      this._updateList(data);
    });

    this.$rootScope.$on('socket:tag:edit', (event, data) => {
      this._updateList(data);
    });

    this.$rootScope.$on('socket:tag:delete', (event, data) => {
      this._deleteFromList(data);
    });
  }
}

export default TagsService;
